import { Controller } from '@hotwired/stimulus';
import { z } from 'zod';
import {
  type TicketInquiryFormData,
  ticketInquiryFormSchema,
} from '../../../../schemas/customer/ticket_inquiry_form_schema';
import { updateRemainingCharacters } from '../../../../utils/customer/character_counter';

export default class extends Controller {
  static override targets = [
    'lastName',
    'firstName',
    'phone',
    'email',
    'companyName',
    'url',
    'inquiryContent',
    'remainingCharacters',
    'privacyPolicy',
    'submitButton',
    'ticketInquiryForm',
  ];

  declare readonly lastNameTarget: HTMLInputElement;
  declare readonly firstNameTarget: HTMLInputElement;
  declare readonly phoneTarget: HTMLInputElement;
  declare readonly emailTarget: HTMLInputElement;
  declare readonly companyNameTarget: HTMLInputElement;
  declare readonly urlTarget: HTMLInputElement;
  declare readonly inquiryContentTarget: HTMLTextAreaElement;
  declare readonly remainingCharactersTarget: HTMLElement;
  declare readonly privacyPolicyTarget: HTMLInputElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly ticketInquiryFormTarget: HTMLElement;

  // ZodスキーマのキーとStimulusターゲットを対応させるマップ
  private get targetsMap(): Record<keyof TicketInquiryFormData, HTMLElement> {
    return {
      lastName: this.lastNameTarget,
      firstName: this.firstNameTarget,
      phone: this.phoneTarget,
      email: this.emailTarget,
      companyName: this.companyNameTarget,
      url: this.urlTarget,
      inquiryContent: this.inquiryContentTarget,
      privacyPolicy: this.privacyPolicyTarget,
    };
  }

  override connect() {
    // 残り文字数表示の初期化
    this.initializeRemainingCharacters();

    // チェックボックスの状態に応じてボタンのdisabled状態を制御
    this.initializeSubmitButtonState();
  }

  /**
   * 送信
   */
  submit(event: Event) {
    // フォームのデフォルト送信動作を停止
    event.preventDefault();

    // フロントエンドでのバリデーションを実行
    if (this.validateForm()) {
      // バリデーション成功時はローディング状態を表示してフォームを送信
      this.submitButtonTarget.disabled = true;

      (this.element as HTMLFormElement).submit();
    } else {
      // バリデーションが失敗した場合は送信を停止
      this.submitButtonTarget.disabled = false;
    }
  }

  validateForm(): boolean {
    try {
      const formData: TicketInquiryFormData = {
        lastName: this.lastNameTarget.value.trim(),
        firstName: this.firstNameTarget.value.trim(),
        phone: this.phoneTarget.value.trim(),
        email: this.emailTarget.value.trim(),
        companyName: this.companyNameTarget?.value?.trim() || '',
        url: this.urlTarget?.value?.trim() || '',
        inquiryContent: this.inquiryContentTarget.value.trim(),
        privacyPolicy: this.privacyPolicyTarget.checked,
      };

      // zodでバリデーション実行
      ticketInquiryFormSchema.parse(formData);

      // バリデーション成功時は全エラーをクリア
      this.clearAllErrors();

      return true;
    } catch (error) {
      console.error('Error in validateForm:', error);
      if (error instanceof z.ZodError) {
        // バリデーションエラーを処理
        this.handleValidationErrors((error as z.ZodError<TicketInquiryFormData>).issues);
      } else {
        console.error('予期しないエラー:', error);
        alert(
          'バリデーションエラーが発生しました: ' +
            (error instanceof Error ? error.message : String(error))
        );
      }
      return false;
    }
  }

  handleValidationErrors(errors: z.ZodError<TicketInquiryFormData>['issues']) {
    this.clearAllErrors();

    // 各エラーフィールドにエラーを表示
    errors.forEach(error => {
      const fieldName = error.path[0] as keyof TicketInquiryFormData;
      const targetElement = this.targetsMap[fieldName];

      // 任意入力のフィールドで、値が空の場合はエラーを表示しない
      if (targetElement instanceof HTMLInputElement && !targetElement.value.trim()) {
        if (fieldName === 'url') {
          return;
        }
      }

      if (targetElement) {
        this.showError(targetElement, error.message);
      }
    });

    // エラーが発生した場合、フォームコンテナの位置にスクロール（ヘッダー分を考慮）
    if (errors.length > 0) {
      const elementRect = this.ticketInquiryFormTarget.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const headerHeight = this.getHeaderHeight(); // レスポンシブ対応のヘッダー高さ

      window.scrollTo({
        top: absoluteElementTop - headerHeight,
        behavior: 'smooth',
      });
    }
  }

  clearAllErrors() {
    const schemaKeys = Object.keys(
      ticketInquiryFormSchema.shape
    ) as (keyof TicketInquiryFormData)[];
    schemaKeys.forEach(fieldName => {
      const targetElement = this.targetsMap[fieldName];
      if (targetElement) {
        this.clearError(targetElement);
      }
    });
  }

  /**
   * フィールドにエラーを表示する
   */
  showError(field: HTMLElement, message: string) {
    // チェックボックス以外の入力フィールドのスタイルを変更
    if (
      (field instanceof HTMLInputElement && field.type !== 'checkbox') ||
      field instanceof HTMLTextAreaElement
    ) {
      field.classList.add('border-error-600', 'border-2');
      field.classList.remove('border-gray-300', 'border');
    }

    const wrapper = field.closest('[data-field-wrapper]');
    const errorMessageContainer = wrapper?.querySelector('[data-error-message-container]');

    if (errorMessageContainer) {
      errorMessageContainer.classList.remove('hidden');
      const errorElement = errorMessageContainer.querySelector('[data-error-message-text]');
      if (errorElement) {
        errorElement.textContent = message;
      }
    }
  }

  /**
   * フィールドのエラーをクリアする
   */
  clearError(field: HTMLElement) {
    // チェックボックス以外の入力フィールドのスタイルを戻す
    if (
      (field instanceof HTMLInputElement && field.type !== 'checkbox') ||
      field instanceof HTMLTextAreaElement
    ) {
      field.classList.add('border-gray-300', 'border');
      field.classList.remove('border-error-600', 'border-2');
    }

    const wrapper = field.closest('[data-field-wrapper]');
    const errorMessageContainer = wrapper?.querySelector('[data-error-message-container]');

    if (errorMessageContainer) {
      errorMessageContainer.classList.add('hidden');
    }
  }

  // フィールドフォーカス時のエラークリア
  clearErrorOnFocus(event: Event) {
    const field = event.target as HTMLInputElement | HTMLTextAreaElement;
    this.clearError(field);
  }

  /**
   * 残り文字数表示の初期化
   */
  initializeRemainingCharacters() {
    // お問い合わせ内容のテキストエリアにinputイベントリスナーを設定
    this.inquiryContentTarget.addEventListener('input', () => {
      this.updateCharacterCount();
    });

    // 初期表示を更新
    this.updateCharacterCount();
  }

  /**
   * 残り文字数を更新
   */
  updateCharacterCount() {
    updateRemainingCharacters(this.inquiryContentTarget, this.remainingCharactersTarget);
  }

  /**
   * 送信ボタンの初期状態を設定し、チェックボックスの状態を監視
   */
  initializeSubmitButtonState() {
    // 初期状態でボタンをdisabledに設定
    this.updateSubmitButtonState();

    // チェックボックスの状態変更を監視
    this.privacyPolicyTarget.addEventListener('change', () => {
      this.updateSubmitButtonState();
    });
  }

  /**
   * チェックボックスの状態に応じて送信ボタンのdisabled状態を更新
   */
  updateSubmitButtonState() {
    const isChecked = this.privacyPolicyTarget.checked;
    this.submitButtonTarget.disabled = !isChecked;
  }

  /**
   * 画面サイズに応じたヘッダーの高さを取得
   */
  private getHeaderHeight(): number {
    // スマートフォン（640px未満）では58px、それ以外は90px
    return window.innerWidth < 640 ? 58 : 90;
  }
}
